import {
    PackageDiscount,
    ProductWithAddOns,
    SelectedPacket
} from 'components/enrollForm/enrollFormTypes'
import { getOwnedPackageCount } from './getOwnedPackageCount'
import { getPackageDiscountAmount } from './packageDiscountMath'
import { Order } from 'storeTypes'

export type RegisteredPackageRow = {
    package_id: string
    name: string
    size_display?: string
    image_url?: string
    quantity: number
    listTotal: number
    discount: number
    netTotal: number
    hidePrice?: boolean
    hasDiscounts: boolean
}

/** Look up catalogue discounts for a package_id from the product list. */
export const getDiscountsForPackageId = (
    packageId: string,
    products: ProductWithAddOns[] = []
): PackageDiscount | undefined => {
    for (const product of products) {
        const price = product.product_prices?.find(
            (productPrice) => productPrice.sf_id === packageId
        )
        if (price) {
            return price.discounts
        }
    }
    return undefined
}

/**
 * Group cart lines by package_id and compute list / discount / net per group.
 *
 * Assumes a uniform catalogue unit price (and name / image / hidePrice) per
 * package_id — every SelectedPacket with the same package_id shares those
 * fields in the enrollment cart. listTotal uses sample.price × quantity.
 */
export const buildRegisteredPackageRows = (
    selectedPackets: SelectedPacket[] = [],
    fullGoalItems: Order[] = [],
    products: ProductWithAddOns[] = []
): RegisteredPackageRow[] => {
    const byPackage = new Map<string, SelectedPacket[]>()

    selectedPackets.forEach((packet) => {
        const packageId = packet.package_id
        if (!packageId) {
            return
        }
        const existing = byPackage.get(packageId) || []
        existing.push(packet)
        byPackage.set(packageId, existing)
    })

    const rows: RegisteredPackageRow[] = []
    byPackage.forEach((packets, packageId) => {
        const sample = packets[0]
        const quantity = packets.length
        const unitPrice = Number(sample.price) || 0
        const listTotal = unitPrice * quantity
        const owned = getOwnedPackageCount(packageId, { fullGoalItems })
        const discounts = getDiscountsForPackageId(packageId, products)
        const discount = getPackageDiscountAmount(discounts, quantity, owned)
        const netTotal = listTotal - discount

        rows.push({
            package_id: packageId,
            name: sample.name || '',
            size_display: sample.size_display,
            image_url: sample.image_url,
            quantity,
            listTotal,
            discount,
            netTotal,
            hidePrice: Boolean(sample.hidePrice),
            hasDiscounts: Boolean(discounts?.tiers?.length)
        })
    })

    return rows
}

export const sumRegisteredPackageNets = (rows: RegisteredPackageRow[]): number =>
    rows.reduce((sum, row) => sum + row.netTotal, 0)

export const sumRegisteredPackageListTotals = (rows: RegisteredPackageRow[]): number =>
    rows.reduce((sum, row) => sum + row.listTotal, 0)
